import { ChevronLeft } from 'lucide-react'
import { useMemo, useState } from 'react'

import { BillingBreakdownHeader } from 'components/billing/BillingBreakdownHeader'
import { InvoiceProvider, useInvoice } from 'components/billing/InvoiceContext'
import { MemberInvoiceDetails } from 'components/billing/MemberInvoiceDetails'
import { StripeInvoiceBreakdown } from 'components/billing/StripeInvoiceBreakdown'
import { ThatchInvoiceBreakdown } from 'components/billing/ThatchInvoiceBreakdown'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { TextButton } from 'slab'
import { formatMonthName, formatPeriodDate, getPriorMonthEnrollmentCutoffDate, type InvoiceType } from 'utils/billing'
import {
  useGetEmployersEmployerInvoicesId,
  useGetEmployersStripeInvoicesId,
} from 'utils/web_api'
import {
  type EmployersEmployerInvoice,
  type EmployersStripeInvoice,
} from 'utils/web_api.schemas'

interface BillingBreakdownProps {
  invoiceId: string
  invoiceType: InvoiceType
  onBack: () => void
  employerInvoice?: EmployersEmployerInvoice
  stripeInvoice?: EmployersStripeInvoice
}

export function BillingBreakdown ({
  invoiceId,
  invoiceType,
  onBack,
  employerInvoice,
  stripeInvoice,
}: BillingBreakdownProps) {
  if (invoiceType === 'stripe') {
    return <StripeBillingBreakdown invoiceId={invoiceId} onBack={onBack} stripeInvoice={stripeInvoice} />
  }
  return <ThatchBillingBreakdown invoiceId={invoiceId} onBack={onBack} employerInvoice={employerInvoice} />
}

function ThatchBillingBreakdown ({
  invoiceId,
  onBack,
  employerInvoice: employerInvoiceProp,
}: {
  invoiceId: string
  onBack: () => void
  employerInvoice?: EmployersEmployerInvoice
}) {
  const [selectedMemberInvoiceId, setSelectedMemberInvoiceId] = useState<string | null>(null)
  const { data: fetchedData, isLoading, isError } = useGetEmployersEmployerInvoicesId(invoiceId, {
    query: {
      enabled: !employerInvoiceProp,
    },
  })
  const data = employerInvoiceProp || fetchedData

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
        <div>
          There was an error fetching invoice details. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  const invoice = data
  const monthName = formatMonthName(invoice.period_end_date)
  const periodEndFormatted = formatPeriodDate(invoice.period_end_date)
  const showPlanYear = (data?.plan_years?.length ?? 0) > 1

  if (selectedMemberInvoiceId) {
    return (
      <InvoiceProvider invoice={invoice}>
        <MemberInvoiceDetailsWrapper
          memberInvoiceId={selectedMemberInvoiceId}
          onBack={() => setSelectedMemberInvoiceId(null)}
        />
      </InvoiceProvider>
    )
  }

  return (
    <InvoiceProvider invoice={invoice}>
      <div>
        <div className='mb-4'>
          <TextButton
            color='gray'
            leftIcon={ChevronLeft}
            onClick={onBack}
          >
            Back to all bills
          </TextButton>
        </div>

        <BillingBreakdownHeader
          titleText={`${monthName} healthcare costs`}
          subheaderText={`Includes costs from ${getPriorMonthEnrollmentCutoffDate(invoice.period_end_date)} - ${periodEndFormatted}`}
          invoiceNumber={invoice.invoice_number || invoice.id}
          invoice={invoice}
          reportDownloadUrl={data?.latest_report?.download_url}
          showPlanYear={showPlanYear}
        />

        <ThatchInvoiceBreakdown
          invoice={invoice}
          onMemberClick={setSelectedMemberInvoiceId}
        />
      </div>
    </InvoiceProvider>
  )
}

function StripeBillingBreakdown ({
  invoiceId,
  onBack,
  stripeInvoice: stripeInvoiceProp,
}: {
  invoiceId: string
  onBack: () => void
  stripeInvoice?: EmployersStripeInvoice
}) {
  const { data: fetchedData, isLoading, isError } = useGetEmployersStripeInvoicesId(invoiceId, {
    query: {
      enabled: !stripeInvoiceProp,
    },
  })
  const data = stripeInvoiceProp || fetchedData

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
        <div>
          There was an error fetching invoice details. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  const invoice = data
  const invoiceDate = new Date(invoice.created)
  const monthName = formatMonthName(invoice.created)
  const firstDayOfMonth = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), 1)
  const firstDayFormatted = firstDayOfMonth.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })

  return (
    <div>
      <div className='mb-4'>
        <TextButton
          color='gray'
          leftIcon={ChevronLeft}
          onClick={onBack}
        >
          Back to all bills
        </TextButton>
      </div>

      <BillingBreakdownHeader
        titleText={`${monthName} platform fees`}
        subheaderText={`Includes enrolled employees as of ${firstDayFormatted}`}
        invoiceNumber={invoice.invoice_number || invoice.id}
        invoice={invoice}
      />

      <StripeInvoiceBreakdown invoice={invoice} />
    </div>
  )
}

function MemberInvoiceDetailsWrapper ({
  memberInvoiceId,
  onBack,
}: {
  memberInvoiceId: string
  onBack: () => void
}) {
  const { allMemberInvoices, isLoadingAllMemberInvoices } = useInvoice()

  const memberInvoice = useMemo(() => {
    return allMemberInvoices?.find((mi) => mi.id === memberInvoiceId) ?? null
  }, [allMemberInvoices, memberInvoiceId])

  if (isLoadingAllMemberInvoices) {
    return (
      <div className='p-6'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  return (
    <MemberInvoiceDetails
      memberInvoice={memberInvoice}
      onBack={onBack}
    />
  )
}

export default BillingBreakdown
